import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  FormControlLabel,
  Grid,
  Modal,
  TextField,
  Typography,
  Button,
  IconButton,
  Radio,
  RadioGroup,
  Checkbox,
  ButtonGroup,
} from "@mui/material";
import DatePickerComponent from "../../../../components/utilities/DatePickerComponent";
import GenericPicker from "../../../../components/utilities/GenericPicker";
import moment from "moment-timezone";
import { postNotification } from "../../../../components/utilities/Notifications";
import { BCPXmark } from "../../../../components/icons/bcp/BCPIcons";

function getReoccurringDates(eventDates, reoccurrenceDetails) {
  let reoccuringDates = [];

  if (reoccurrenceDetails?.customReoccurrence) {
    if (reoccurrenceDetails.customReoccurrenceIncrement !== undefined) {
      reoccurrenceDetails.customReoccurrenceIncrement = parseInt(
        reoccurrenceDetails.customReoccurrenceIncrement
      );
    }
    if (reoccurrenceDetails.customNumOfReoccurrences !== undefined) {
      reoccurrenceDetails.customNumOfReoccurrences = parseInt(
        reoccurrenceDetails.customNumOfReoccurrences
      );
    }
    if (reoccurrenceDetails.customReoccurrenceEndDate) {
      reoccurrenceDetails.customReoccurrenceEndDate = moment(
        reoccurrenceDetails.customReoccurrenceEndDate
      );
    }

    if (reoccurrenceDetails.customReoccurrenceLength === "week(s)") {
      for (let i = 0; i < 7; i++) {
        let eventDate = moment(eventDates?.eventDate);
        if (i > 0) eventDate.add(i, "days");

        let eventEndDate = moment(eventDates?.eventEndDate);
        if (i > 0) eventEndDate.add(i, "days");

        let refundDate = moment(eventDates?.refundDate);
        if (i > 0) refundDate.add(i, "days");

        if (
          reoccurrenceDetails?.customWeeklyDays?.[
            eventDate.format("dddd").toLowerCase()
          ]
        ) {
          reoccuringDates.push({
            eventDate: eventDate.format("YYYY-MM-DDTHH:mm"),
            eventEndDate: eventEndDate.format("YYYY-MM-DDTHH:mm"),
            refundDate: refundDate.format("YYYY-MM-DDTHH:mm"),
          });
          let iterations = 1;
          while (
            (reoccurrenceDetails.customReoccurrenceEnds === "afterNum" &&
              reoccurrenceDetails.customNumOfReoccurrences > iterations) ||
            (reoccurrenceDetails.customReoccurrenceEnds === "onDate" &&
              eventDate.isSameOrBefore(
                reoccurrenceDetails.customReoccurrenceEndDate
              )) ||
            (reoccurrenceDetails.customReoccurrenceEnds === "afterDate" &&
              reoccurrenceDetails.customReoccurrenceEndDate.isAfter(eventDate))
          ) {
            iterations++;
            eventDate.add(
              reoccurrenceDetails.customReoccurrenceIncrement,
              "weeks"
            );
            eventEndDate.add(
              reoccurrenceDetails.customReoccurrenceIncrement,
              "weeks"
            );
            refundDate.add(
              reoccurrenceDetails.customReoccurrenceIncrement,
              "weeks"
            );

            reoccuringDates.push({
              eventDate: eventDate.format("YYYY-MM-DDTHH:mm"),
              eventEndDate: eventEndDate.format("YYYY-MM-DDTHH:mm"),
              refundDate: refundDate.format("YYYY-MM-DDTHH:mm"),
            });
          }
        }
      }
    }

    if (reoccurrenceDetails.customReoccurrenceEnds === "onDate") {
      reoccuringDates = reoccuringDates.filter((event) => {
        return moment(event.eventDate).isSameOrBefore(
          moment(reoccurrenceDetails.customReoccurrenceEndDate).format(
            "YYYY-MM-DDTHH:mm"
          )
        );
      });
    }
  }

  return reoccuringDates.sort((a, b) => {
    let am = moment(a.eventDate);
    let bm = moment(b.eventDate);
    return am.diff(bm);
  });
}

export default function ReoccurringEventModal(props) {
  const { t } = useTranslation();
  const { values, setFieldValue, reoccurringOpen, setReoccurringOpen } = props;

  const [simpleReoccurrenceLength, setSimpleReoccurrenceLength] = useState("");
  const [customReoccurrence, setCustomReoccurrence] = useState(false);
  const [customReoccurrenceIncrement, setCustomReoccurrenceIncrement] =
    useState(1);
  const [customReoccurrenceLength, setCustomReoccurrenceLength] =
    useState("day(s)");
  const [customReoccurrenceEnds, setCustomReoccurrenceEnds] = useState(null);
  const [customNumOfReoccurrences, setCustomNumOfReoccurrences] = useState(1);
  const [customReoccurrenceEndDate, setCustomReoccurrenceEndDate] =
    useState(null);
  const [customWeeklyDays, setCustomWeeklyDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });

  let eventData = {
    simpleReoccurrenceLength,
    customReoccurrence,
    customReoccurrenceIncrement,
    customReoccurrenceLength,
    customReoccurrenceEnds,
    customNumOfReoccurrences,
    customReoccurrenceEndDate,
    customWeeklyDays,
  };

  let checkDates = [];

  // eventWeekNum returns to us, the current week number relative to the month. If its the first week of March then it'll return 1. If its the third week, it'll return three. Used for months
  useEffect(() => {
    generateDates();

    if (checkDates.length > 1) {
      postNotification(
        "warning",
        t("notifications.warnings.reoccurringEventDatesUpdated")
      );
    }
    // eslint-disable-next-line
  }, [values.eventDate, values.eventEndDate, values.refundDate]);

  // Will generate date objects and set them into an array
  function generateDates() {
    let dates = [];

    function createDatesByNumOfOccurrences(
      numOfReoccurrences,
      amountOfTime,
      reoccurrenceFrequency
    ) {
      let date = values.eventDate;
      let endDate = values.eventEndDate;
      let refundDate = values.refundDate;

      for (let i = 0; i < numOfReoccurrences; i++) {
        if (i !== 0) {
          date = moment(date)
            .add(reoccurrenceFrequency, amountOfTime)
            .format("YYYY-MM-DDTHH:mm");
          endDate = moment(endDate)
            .add(reoccurrenceFrequency, amountOfTime)
            .format("YYYY-MM-DDTHH:mm");
          refundDate = moment(refundDate)
            .add(reoccurrenceFrequency, amountOfTime)
            .format("YYYY-MM-DDTHH:mm");
        }

        const newDateObject = {
          eventDate: date,
          eventEndDate: endDate,
          refundDate: refundDate,
        };

        dates.push(newDateObject);
      }
    }

    function createDatesBySpecificDate(
      userEndDate,
      amountOfTime,
      reoccurrenceFrequency,
      onOrAfter,
      customWeeklyDays
    ) {
      let eventDate = values.eventDate;
      let eventEndDate = values.eventEndDate;
      let refundDate = values.refundDate;
      let newDateObject = {
        eventDate: eventDate,
        eventEndDate: eventEndDate,
        refundDate: refundDate,
      };

      dates.push(newDateObject);

      let run = true;
      if (customWeeklyDays) {
        function generateDateObjects(
          customWeeklyDays,
          reoccurrenceFrequency,
          userEndDate,
          newDateObject
        ) {
          const eventDate = moment(newDateObject.eventDate);
          const eventEndDate = moment(newDateObject.eventEndDate);
          const refundDate = moment(newDateObject.refundDate);
          const endDateTime = moment(userEndDate);
          let currentDay = eventDate.clone();
          let weekNum = 1;

          while (currentDay.isSameOrBefore(endDateTime)) {
            if (customWeeklyDays[currentDay.format("dddd").toLowerCase()]) {
              dates.push({
                eventDate: currentDay.format("YYYY-MM-DDTHH:mm"),
                eventEndDate: eventEndDate.format("YYYY-MM-DDTHH:mm"),
                refundDate: refundDate.format("YYYY-MM-DDTHH:mm"),
              });
            }

            if (weekNum === reoccurrenceFrequency) {
              currentDay.add(reoccurrenceFrequency, "weeks");
              eventEndDate.add(reoccurrenceFrequency, "weeks");
              refundDate.add(reoccurrenceFrequency, "weeks");

              weekNum = 1;
            }

            if (currentDay.isoWeek() === 1) {
              weekNum++;
            } else {
              currentDay.add(1, "day");
              eventEndDate.add(1, "day");
              refundDate.add(1, "day");
            }
          }
        }

        generateDateObjects(
          customWeeklyDays,
          parseInt(customReoccurrenceIncrement),
          customReoccurrenceEndDate,
          newDateObject
        );
      } else {
        while (run) {
          eventDate = moment(eventDate)
            .add(reoccurrenceFrequency, amountOfTime)
            .format("YYYY-MM-DDTHH:mm");
          eventEndDate = moment(eventEndDate)
            .add(reoccurrenceFrequency, amountOfTime)
            .format("YYYY-MM-DDTHH:mm");
          refundDate = moment(refundDate)
            .add(reoccurrenceFrequency, amountOfTime)
            .format("YYYY-MM-DDTHH:mm");
          if (onOrAfter === "onDate") {
            if (moment(eventDate).isBefore(userEndDate)) {
              newDateObject = {
                eventDate: eventDate,
                eventEndDate: eventEndDate,
                refundDate: refundDate,
              };
              dates.push(newDateObject);
            } else {
              run = false;
              return;
            }
          } else {
            if (moment(eventDate).isBefore(moment(userEndDate).add(1, "day"))) {
              newDateObject = {
                eventDate: eventDate,
                eventEndDate: eventEndDate,
                refundDate: refundDate,
              };
              dates.push(newDateObject);
            } else {
              run = false;
              return;
            }
          }
        }
        return dates;
      }
    }

    if (customReoccurrence) {
      if (customReoccurrenceLength === "day(s)") {
        if (
          customReoccurrenceEnds === "onDate" ||
          customReoccurrenceEnds === "afterDate"
        ) {
          createDatesBySpecificDate(
            customReoccurrenceEndDate,
            "days",
            customReoccurrenceIncrement,
            customReoccurrenceEnds
          );
        } else {
          createDatesByNumOfOccurrences(
            customNumOfReoccurrences,
            "days",
            customReoccurrenceIncrement
          );
        }
      } else if (customReoccurrenceLength === "week(s)") {
        dates = getReoccurringDates(values, eventData);
      } else if (customReoccurrenceLength.includes("month(s) on day")) {
        if (
          customReoccurrenceEnds === "onDate" ||
          customReoccurrenceEnds === "afterDate"
        ) {
          createDatesBySpecificDate(
            customReoccurrenceEndDate,
            "month",
            customReoccurrenceIncrement,
            customReoccurrenceEnds
          );
        } else {
          createDatesByNumOfOccurrences(
            customNumOfReoccurrences,
            "month",
            customReoccurrenceIncrement
          );
        }
      }
    } else {
      if (simpleReoccurrenceLength === "Daily") {
        if (
          customReoccurrenceEnds === "onDate" ||
          customReoccurrenceEnds === "afterDate"
        ) {
          createDatesBySpecificDate(
            customReoccurrenceEndDate,
            "day",
            1,
            customReoccurrenceEnds
          );
        } else {
          createDatesByNumOfOccurrences(customNumOfReoccurrences, "day", 1);
        }
      } else if (simpleReoccurrenceLength.includes("Weekly")) {
        if (
          customReoccurrenceEnds === "onDate" ||
          customReoccurrenceEnds === "afterDate"
        ) {
          createDatesBySpecificDate(
            customReoccurrenceEndDate,
            "week",
            1,
            customReoccurrenceEnds
          );
        } else {
          createDatesByNumOfOccurrences(customNumOfReoccurrences, "week", 1);
        }
      } else if (simpleReoccurrenceLength.includes("Monthly on day")) {
        if (
          customReoccurrenceEnds === "onDate" ||
          customReoccurrenceEnds === "afterDate"
        ) {
          createDatesBySpecificDate(
            customReoccurrenceEndDate,
            "month",
            1,
            customReoccurrenceEnds
          );
        } else {
          createDatesByNumOfOccurrences(customNumOfReoccurrences, "month", 1);
        }
      }
    }

    const originalEventDate = {
      eventDate: values.eventDate,
      eventEndDate: values.eventEndDate,
      refundDate: values.refundDate,
    };

    if (dates[0]?.eventDate !== originalEventDate.eventDate) {
      dates.unshift({
        eventDate: values.eventDate,
        eventEndDate: values.eventEndDate,
        refundDate: values.refundDate,
      });
    }

    checkDates = [...dates];

    setFieldValue("reoccurringDates", dates);
  }

  return (
    <>
      <Modal
        disableAutoFocus
        open={reoccurringOpen}
        onClose={() => {
          setReoccurringOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setReoccurringOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid container xs={12} spacing={1}>
            <Grid item xs={12}>
              <Typography>
                Recurrence Details{" "}
                {customReoccurrence ? "- Custom" : "- Standard"}
              </Typography>
            </Grid>

            {!customReoccurrence && (
              <>
                <Grid item xs={12}>
                  <GenericPicker
                    name="simpleReoccurrenceLength"
                    label="Event repeats"
                    value={simpleReoccurrenceLength}
                    handleChange={(e) => {
                      setSimpleReoccurrenceLength(e.target.value);
                    }}
                    list={[
                      "Daily",
                      `Weekly on ${moment(values.eventDate).format("dddd")}`,
                      `Monthly on day ${moment(values.eventDate).format("DD")}`,
                    ]}
                    inclAny={false}
                  />
                </Grid>
              </>
            )}

            {customReoccurrence && (
              <>
                <Grid item xs={6}>
                  <TextField
                    name="customReoccurrenceIncrement"
                    value={customReoccurrenceIncrement}
                    type="number"
                    label="Repeat every"
                    InputProps={{
                      inputProps: { min: 1 },
                    }}
                    onChange={(e) => {
                      setCustomReoccurrenceIncrement(e.target.value);
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <GenericPicker
                    name="customReoccurrenceLength"
                    label="Length of time"
                    value={customReoccurrenceLength}
                    handleChange={(e) => {
                      setCustomReoccurrenceLength(e.target.value);
                    }}
                    list={[
                      "day(s)",
                      "week(s)",
                      `month(s) on day ${moment(values.eventDate).format(
                        "DD"
                      )}`,
                    ]}
                    inclAny={false}
                  />
                </Grid>

                {customReoccurrenceLength === "week(s)" && (
                  <Grid item xs={12}>
                    <Typography gutterBottom sx={{ textAlign: "center" }}>
                      Events will take place on these days:
                    </Typography>
                    <Grid
                      container
                      spacing={1}
                      columns={14}
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                        let day = moment().day(d);
                        return (
                          <Grid item xs={2} key={`Day ${d}`}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  onClick={(e) => {
                                    setCustomWeeklyDays({
                                      ...customWeeklyDays,
                                      [day.format("dddd").toLowerCase()]:
                                        !customWeeklyDays[
                                          day.format("dddd").toLowerCase()
                                        ],
                                    });
                                  }}
                                  name={`customWeeklyDays.${day
                                    .format("dddd")
                                    .toLowerCase()}`}
                                  checked={
                                    customWeeklyDays[
                                      day.format("dddd").toLowerCase()
                                    ]
                                  }
                                />
                              }
                              labelPlacement="top"
                              label={day.format("ddd")}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <Typography variant="body1">Repeat ends: </Typography>
              <RadioGroup
                name="customReoccurrenceEnds"
                value={customReoccurrenceEnds}
                defaultValue="first"
              >
                <FormControlLabel
                  value="onDate"
                  label="On specific date"
                  onChange={(e) => {
                    setCustomReoccurrenceEnds(e.target.value);
                  }}
                  control={<Radio />}
                />

                { (
                  <FormControlLabel
                    value="afterDate"
                    label="After specific date"
                    onChange={(e) => {
                      setCustomReoccurrenceEnds(e.target.value);
                    }}
                    control={<Radio />}
                  />
                )}

                <FormControlLabel
                  value="afterNum"
                  label="After number of occurrences"
                  onChange={(e) => {
                    setCustomReoccurrenceEnds(e.target.value);
                  }}
                  control={<Radio />}
                />
              </RadioGroup>
            </Grid>

            {(customReoccurrenceEnds === "onDate" ||
              customReoccurrenceEnds === "afterDate") && (
              <Grid item xs={12}>
                <DatePickerComponent
                  name="customReoccurrenceEndDate"
                  label={
                    customReoccurrenceEnds === "onDate"
                      ? "End date"
                      : "End after date"
                  }
                  value={customReoccurrenceEndDate}
                  setFieldValue={(name, date) => {
                    setCustomReoccurrenceEndDate(
                      moment(date).format("YYYY-MM-DDTHH:mm")
                    );
                  }}
                />
              </Grid>
            )}

            {customReoccurrenceEnds === "afterNum" && (
              <Grid item xs={12}>
                <TextField
                  label="Number of occurrences"
                  type="number"
                  value={customNumOfReoccurrences}
                  onChange={(e) => {
                    if (e.target.value > 100) {
                      setCustomNumOfReoccurrences(100);
                    } else {
                      setCustomNumOfReoccurrences(e.target.value);
                    }
                  }}
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <ButtonGroup fullWidth variant="outlined">
                <Button
                  onClick={() => {
                    setCustomReoccurrence(!customReoccurrence);
                  }}
                >
                  {customReoccurrence
                    ? "Standard Recurrence"
                    : "Custom Recurrence"}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (customReoccurrence) {
                      if (
                        customReoccurrenceIncrement === "" ||
                        parseInt(customReoccurrenceIncrement) <= 0
                      ) {
                        postNotification(
                          "error",
                          "Missing a value for 'Repeat Every'"
                        );
                        return;
                      }

                      if (customReoccurrenceEnds === "afterNum") {
                        if (
                          customNumOfReoccurrences === "" ||
                          parseInt(customNumOfReoccurrences) <= 0
                        ) {
                          postNotification(
                            "error",
                            "Missing a value for 'Number of occurrences'"
                          );
                          return;
                        }
                      }
                    }
                    setReoccurringOpen(false);
                    setCustomReoccurrence(customReoccurrence);
                    generateDates();

                    if (checkDates.length > 1) {
                      postNotification(
                        "success",
                        "Recurring dates have been created."
                      );
                    } else {
                      postNotification(
                        "error",
                        "Recurrence details are incomplete."
                      );
                    }
                  }}
                >
                  Submit Changes
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
}
